import React, { Suspense, lazy, createContext, useContext, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

// Lazy-load heavy pages for better initial load performance
const Landing = lazy(() => import("./pages/Landing"));
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Analysis = lazy(() => import("./pages/Analysis"));
const Compare = lazy(() => import("./pages/Compare"));
const BrainMapping = lazy(() => import("./pages/BrainMapping"));
const LiveAudienceSimulation = lazy(() => import("./pages/LiveAudienceSimulation"));
const ReelSimulator = lazy(() => import("./pages/ReelSimulator"));
const SimulationsTab = lazy(() => import("./pages/SimulationsTab"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Settings = lazy(() => import("./pages/Settings"));
const AdminConsole = lazy(() => import("./pages/AdminConsole"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const LatestVideoRedirect = lazy(() => import("./components/LatestVideoRedirect"));

// Legal pages
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy"));
const AcceptableUsePolicy = lazy(() => import("./pages/legal/AcceptableUsePolicy"));
const RefundPolicy = lazy(() => import("./pages/legal/RefundPolicy"));
const SecurityPolicy = lazy(() => import("./pages/legal/SecurityPolicy"));
const AIDisclaimer = lazy(() => import("./pages/legal/AIDisclaimer"));
const DataProcessingAgreement = lazy(() => import("./pages/legal/DataProcessingAgreement"));
const ContactPage = lazy(() => import("./pages/legal/ContactPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
export const API = `${BACKEND_URL}/api`;

// Use Authorization: Bearer <token> from localStorage (avoids CORS credentials issues)
axios.defaults.withCredentials = false;
axios.interceptors.request.use((config) => {
  const t = localStorage.getItem("token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const getAuthToken = () => localStorage.getItem("token") || localStorage.getItem("session_token") || "";

const OBSERVABILITY_DYNAMIC_PATHS = ["/analysis/", "/brain-mapping/", "/audience/"];

function sanitizeObservabilityEvent(event) {
  const url = new URL(event.url);

  if (url.pathname.startsWith("/admin")) {
    return null;
  }

  url.hash = "";
  url.search = "";

  for (const prefix of OBSERVABILITY_DYNAMIC_PATHS) {
    if (url.pathname.startsWith(prefix)) {
      url.pathname = `${prefix}[id]`;
      break;
    }
  }

  return {
    ...event,
    url: url.toString(),
  };
}

// -------- Error Boundary --------
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#030303] text-white px-6">
          <div className="glass rounded-2xl p-10 text-center max-w-md">
            <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
              <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
            </div>
            <h2 className="text-xl font-medium mb-2">Something went wrong</h2>
            <p className="text-sm text-zinc-400 mb-6">An unexpected error occurred. Try refreshing the page.</p>
            <button
               onClick={() => window.location.reload()}
               className="px-6 py-2.5 rounded-full bg-white text-black font-medium text-sm hover:bg-zinc-100 transition-colors"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// -------- Page Loading Spinner --------
function PageSpinner() {
  return (
    <div className="min-h-screen bg-[#030303]">
      <div className="fixed top-0 left-0 right-0 z-[60] h-0.5 overflow-hidden">
        <div
          className="h-full w-full bg-gradient-to-r from-violet-500 via-cyan-400 to-violet-500"
          style={{
            backgroundSize: "200% 100%",
            animation: "shimmer 1.2s ease-in-out infinite",
          }}
        />
      </div>
    </div>
  );
}

// -------- Auth Context --------
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.error(err);
    }
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("session_token");
  };

  return (
    <AuthContext.Provider value={{ user, loading, setUser, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-10 h-10 rounded-full border-2 border-violet-500/30 border-t-violet-500 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/simulations" element={<ProtectedRoute><SimulationsTab /></ProtectedRoute>} />
        <Route path="/analysis/:id" element={<ProtectedRoute><Analysis /></ProtectedRoute>} />
        <Route path="/compare" element={<ProtectedRoute><Compare /></ProtectedRoute>} />
        <Route path="/brain-mapping/:id" element={<ProtectedRoute><BrainMapping /></ProtectedRoute>} />
        <Route path="/audience/:id" element={<ProtectedRoute><LiveAudienceSimulation /></ProtectedRoute>} />
        <Route path="/psyrogram" element={<ProtectedRoute><LatestVideoRedirect pathPrefix="/psyrogram" label="Open Psyrogram" /></ProtectedRoute>} />
        <Route path="/psyrogram/:id" element={<ProtectedRoute><ReelSimulator /></ProtectedRoute>} />
        <Route path="/brain-mapping" element={<ProtectedRoute><LatestVideoRedirect pathPrefix="/brain-mapping" label="Open Brain View" /></ProtectedRoute>} />
        <Route path="/audience" element={<ProtectedRoute><LatestVideoRedirect pathPrefix="/audience" label="Open audience simulation" /></ProtectedRoute>} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/admin" element={<AdminConsole />} />
        
        {/* Legal & Policy Pages */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/acceptable-use" element={<AcceptableUsePolicy />} />
        <Route path="/refunds" element={<RefundPolicy />} />
        <Route path="/security" element={<SecurityPolicy />} />
        <Route path="/ai-disclaimer" element={<AIDisclaimer />} />
        <Route path="/dpa" element={<DataProcessingAgreement />} />
        <Route path="/contact" element={<ContactPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  // Read GOOGLE_CLIENT_ID from environment variables
  const clientId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || "").trim();
  const appShell = (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" position="bottom-right" richColors />
        <Analytics beforeSend={sanitizeObservabilityEvent} />
        <SpeedInsights beforeSend={sanitizeObservabilityEvent} />
      </AuthProvider>
    </BrowserRouter>
  );

  return (
    <div className="App">
      <ErrorBoundary>
        {clientId ? <GoogleOAuthProvider clientId={clientId}>{appShell}</GoogleOAuthProvider> : appShell}
      </ErrorBoundary>
    </div>
  );
}

export default App;
